import React, { useState, useCallback, useRef, useEffect } from "react";
import { AlertTriangle, CheckCircle2, Search, Plus, X, XCircle } from "lucide-react";
import { stockSearchService, SearchResult } from "../../../../services/stockSearchService";
import {
  classifyStockLiquidity,
  StockLiquidityStatus,
  STOCK_LIQUIDITY_DIALOG_TITLE,
  STOCK_LIQUIDITY_LABEL,
  STOCK_LIQUIDITY_MESSAGE,
} from "../../../../utils/stockLiquidityGate";
import ConfirmationModal from "../../../../components/ConfirmationModal";

export interface UnifiedSearchBarProps {
  existingConIds: Set<number>;
  entityLabel: string;
  filterText: string;
  onFilterTextChange: (text: string) => void;
  onAddStock: (result: SearchResult) => void;
  /**
   * Applies the PWA-2597 liquidity gate: per-row status icons, a confirmation
   * dialog for missing data, and a hard block on stocks the platform cannot
   * trade. Opt-in because this bar also backs surfaces the gate must not touch
   * (contract exclusions). Defaults off.
   */
  enforceLiquidityGate?: boolean;
  appearance?: "default" | "alpha";
}

/**
 * Icon plus its accessible text alternative. The description is exposed three
 * ways deliberately -- `title` for mouse hover, `sr-only` text for screen
 * readers, and (for selectable rows) as part of the button's accessible name so
 * a keyboard user hears why a row is blocked before activating it.
 */
const StockLiquidityIndicator: React.FC<{
  status: StockLiquidityStatus;
  appearance?: "default" | "alpha";
}> = ({ status, appearance = "default" }) => {
  const label = STOCK_LIQUIDITY_LABEL[status];
  const message = STOCK_LIQUIDITY_MESSAGE[status];
  const description = message ? `${label}. ${message}` : label;

  const Icon = status === "GREEN" ? CheckCircle2 : status === "YELLOW" ? AlertTriangle : XCircle;
  const color = appearance === "alpha"
    ? status === "GREEN"
      ? "text-v7-success"
      : status === "YELLOW"
        ? "text-v7-bronze-dark"
        : "text-v7-rust"
    : status === "GREEN"
      ? "text-green-600 dark:text-green-500"
      : status === "YELLOW"
        ? "text-yellow-600 dark:text-yellow-500"
        : "text-red-600 dark:text-red-500";

  return (
    <span className="inline-flex items-center" title={description}>
      <Icon className={`w-4 h-4 ${color}`} aria-hidden="true" />
      <span className="sr-only">{description}</span>
    </span>
  );
};

const US_EXCHANGE_PRIORITY = [
  "NASDAQ",
  "NASDAQGS",
  "NASDAQGM",
  "NASDAQCM",
  "NMS",
  "ISLAND",
  "NYSE",
  "NYSEARCA",
  "ARCA",
  "NYSEAMERICAN",
  "AMEX",
  "NYSEMKT",
  "BATS",
  "BZX",
  "BYX",
  "EDGA",
  "EDGX",
  "CBOEBZX",
  "CBOEEDGX",
  "IEX",
  "MEMX",
  "LTSE",
  "OTCQX",
  "OTCQB",
  "OTC",
  "PINK",
];

const EXCHANGE_PRIORITY_MAP = new Map(
  US_EXCHANGE_PRIORITY.map((exchange, index) => [exchange, index])
);

const normalizeExchange = (exchange?: string): string =>
  (exchange ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");

const getExchangePriority = (result: SearchResult): number => {
  const primary = normalizeExchange(result.primaryExchange);
  const exchange = normalizeExchange(result.exchange);

  const primaryRank = primary ? EXCHANGE_PRIORITY_MAP.get(primary) : undefined;
  if (primaryRank !== undefined) return primaryRank;

  const exchangeRank = exchange ? EXCHANGE_PRIORITY_MAP.get(exchange) : undefined;
  if (exchangeRank !== undefined) return exchangeRank;

  return Number.POSITIVE_INFINITY;
};

export const sortUsExchangesFirst = (results: SearchResult[]): SearchResult[] =>
  results
    .map((result, index) => ({ result, index, rank: getExchangePriority(result) }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map(({ result }) => result);

/**
 * Combined search-to-add and filter-existing input.
 *
 * While the dropdown is open or a search is in progress the parent's
 * filterText is kept empty so the table shows ALL rows (V2 parity).
 * When the dropdown closes the typed text is propagated as filterText
 * so the table filters by ticker/name.
 */
const UnifiedSearchBar: React.FC<UnifiedSearchBarProps> = ({
  existingConIds,
  entityLabel,
  filterText,
  onFilterTextChange,
  onAddStock,
  enforceLiquidityGate = false,
  appearance = "default",
}) => {
  const isAlpha = appearance === "alpha";
  // Component-owned query state (what the user sees in the input).
  // Parent's filterText is only updated when the dropdown is NOT active.
  const [inputValue, setInputValue] = useState(filterText);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Track whether the dropdown/search is active to suppress parent filter
  const dropdownActiveRef = useRef(false);
  // Non-passing selection awaiting the user's response. RED never resolves to an add.
  const [gateDialog, setGateDialog] = useState<
    { result: SearchResult; status: Exclude<StockLiquidityStatus, "GREEN"> } | null
  >(null);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  // Sync inputValue when parent clears filterText externally
  useEffect(() => {
    if (filterText === "" && inputValue !== "" && !dropdownActiveRef.current) {
      setInputValue("");
    }
    // Only react to parent-driven resets
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterText]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      // Clicks inside the gate dialog land outside the container -- leave the
      // dropdown state alone until the user has answered it.
      if (gateDialog) return;
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        if (showDropdown) {
          setShowDropdown(false);
          dropdownActiveRef.current = false;
          // Dropdown dismissed -- propagate inputValue as filter text
          onFilterTextChange(inputValue);
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDropdown, inputValue, onFilterTextChange, gateDialog]);

  const performSearch = useCallback(async (query: string) => {
    if (query.trim().length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      dropdownActiveRef.current = false;
      setIsSearching(false);
      // No search pending -- propagate as filter
      onFilterTextChange(query);
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setIsSearching(true);
    dropdownActiveRef.current = true;
    // While search is active, suppress table filtering
    onFilterTextChange("");
    try {
      const results = await stockSearchService.searchStocks(
        query.trim(),
        false,
        20,
        controller.signal
      );
      if (!controller.signal.aborted) {
        const rankedResults = sortUsExchangesFirst(results);
        setSearchResults(rankedResults);
        setShowDropdown(rankedResults.length > 0);
        dropdownActiveRef.current = rankedResults.length > 0;
        if (rankedResults.length === 0) {
          // No results found -- let filter text apply
          onFilterTextChange(query);
        }
      }
    } catch {
      if (!controller.signal.aborted) {
        dropdownActiveRef.current = false;
        onFilterTextChange(query);
      }
    }
    if (!controller.signal.aborted) {
      setIsSearching(false);
    }
  }, [onFilterTextChange]);

  const handleChange = useCallback(
    (value: string) => {
      setInputValue(value);
      // While the user is actively typing and a search will fire,
      // suppress the parent filter so the table shows all rows.
      if (value.trim().length >= 2) {
        onFilterTextChange("");
        dropdownActiveRef.current = true;
      } else {
        // Short query -- no search will fire, apply as filter
        onFilterTextChange(value);
        dropdownActiveRef.current = false;
      }
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => {
        performSearch(value);
      }, 500);
    },
    [onFilterTextChange, performSearch]
  );

  const handleClear = useCallback(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    abortRef.current?.abort();
    setInputValue("");
    onFilterTextChange("");
    setSearchResults([]);
    setShowDropdown(false);
    setIsSearching(false);
    dropdownActiveRef.current = false;
  }, [onFilterTextChange]);

  const handleAdd = useCallback(
    (result: SearchResult) => {
      // Enforced on selection, not just visually -- this is the single path a
      // mouse click, an Enter keypress on the focused row, and a programmatic
      // activation all run through.
      if (enforceLiquidityGate) {
        const status = classifyStockLiquidity(result);
        if (status !== "GREEN") {
          setGateDialog({ result, status });
          return;
        }
      }
      onAddStock(result);
      handleClear();
    },
    [enforceLiquidityGate, onAddStock, handleClear]
  );

  const handleGateConfirm = useCallback(() => {
    // RED has no confirm path. Guarded here as well as in the render so the
    // blocked stock cannot be added even if the confirm handler is reached.
    if (!gateDialog || gateDialog.status !== "YELLOW") return;
    const { result } = gateDialog;
    setGateDialog(null);
    onAddStock(result);
    handleClear();
  }, [gateDialog, onAddStock, handleClear]);

  const rowStatus = useCallback(
    (result: SearchResult): StockLiquidityStatus | null =>
      enforceLiquidityGate ? classifyStockLiquidity(result) : null,
    [enforceLiquidityGate]
  );

  // Split results into existing and new
  const existingResults = searchResults.filter(
    (r) => r.conId !== undefined && existingConIds.has(r.conId)
  );
  const newResults = searchResults.filter(
    (r) => r.conId === undefined || !existingConIds.has(r.conId)
  );

  return (
    <div ref={containerRef} className="relative flex-1">
      <div className="relative">
        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isAlpha ? "text-v7-ink/40" : "text-gray-400 dark:text-rp-grey/60"}`} />
        <input
          type="text"
          value={inputValue}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => {
            if (searchResults.length > 0) {
              setShowDropdown(true);
              dropdownActiveRef.current = true;
              onFilterTextChange("");
            }
          }}
          placeholder="Search or add stocks..."
          className={isAlpha
            ? "w-full pl-10 pr-8 py-2 rounded-v7-control border text-sm border-v7-ink/20 bg-v7-white text-v7-ink placeholder:text-v7-ink/40 focus:outline-none focus:ring-1 focus:ring-v7-bronze-dark focus:border-v7-bronze-dark"
            : "w-full pl-10 pr-8 py-2 rounded-lg border text-sm bg-white dark:bg-rp-black border-gray-200 dark:border-rp-separator text-gray-900 dark:text-rp-grey placeholder-gray-400 dark:placeholder-rp-grey/40 focus:outline-none focus:ring-1 focus:ring-rp-amber focus:border-rp-amber"}
        />
        {inputValue && (
          <button
            onClick={handleClear}
            className={`absolute right-3 top-1/2 -translate-y-1/2 ${isAlpha ? "text-v7-ink/40 hover:text-v7-ink" : "text-gray-400 dark:text-rp-grey/60 hover:text-gray-600 dark:hover:text-rp-grey"} transition-colors`}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
        {isSearching && (
          <span className={`absolute right-8 top-1/2 -translate-y-1/2 text-xs ${isAlpha ? "text-v7-ink/40" : "text-gray-400 dark:text-rp-grey/40"}`}>
            Searching...
          </span>
        )}
      </div>

      {showDropdown && (
        <div className={isAlpha
          ? "absolute z-20 w-full mt-1 rounded-v7-panel shadow-[0_18px_40px_rgba(17,29,50,0.22)] border max-h-80 overflow-y-auto bg-v7-white border-v7-ink/15"
          : "absolute z-20 w-full mt-1 rounded-lg shadow-lg border max-h-80 overflow-y-auto bg-white dark:bg-rp-black border-gray-200 dark:border-rp-separator"}>
          {isSearching && searchResults.length === 0 && (
            <div className={`px-4 py-3 text-xs ${isAlpha ? "text-v7-ink/40" : "text-gray-400 dark:text-rp-grey/60"}`}>Searching...</div>
          )}
          {!isSearching && searchResults.length === 0 && (
            <div className={`px-4 py-3 text-xs ${isAlpha ? "text-v7-ink/40" : "text-gray-400 dark:text-rp-grey/60"}`}>No stocks found</div>
          )}
          {existingResults.map((r) => (
            <div
              key={r.conId}
              className={`flex items-center justify-between px-4 py-2 text-sm cursor-default border-b last:border-b-0 ${isAlpha ? "border-v7-ink/10" : "border-gray-100 dark:border-rp-separator/50"}`}
            >
              <div>
                <span className={`font-medium ${isAlpha ? "text-v7-ink" : "text-gray-900 dark:text-white"}`}>{r.ticker}</span>
                <span className={`ml-2 ${isAlpha ? "text-v7-ink-soft" : "text-gray-500 dark:text-rp-grey"}`}>{r.name}</span>
                {r.primaryExchange && (
                  <span className={`ml-2 text-xs ${isAlpha ? "text-v7-ink/40" : "text-gray-400 dark:text-rp-grey/40"}`}>{r.primaryExchange}</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {(() => {
                  const status = rowStatus(r);
                  return status ? <StockLiquidityIndicator status={status} appearance={appearance} /> : null;
                })()}
                <span className={`text-[10px] px-2 py-0.5 rounded ${isAlpha ? "bg-v7-ink/[0.06] text-v7-ink-soft" : "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300"}`}>
                  In {entityLabel}
                </span>
              </div>
            </div>
          ))}
          {newResults.map((r, i) => {
            const status = rowStatus(r);
            return (
            <button
              key={r.conId ?? `new-${i}`}
              onClick={() => handleAdd(r)}
              aria-disabled={status === "RED" ? true : undefined}
              className={`w-full flex items-center justify-between px-4 py-2 text-sm ${isAlpha ? "hover:bg-v7-paper" : "hover:bg-gray-50 dark:hover:bg-rp-separator/60"} transition-colors border-b last:border-b-0 ${isAlpha ? "border-v7-ink/10" : "border-gray-100 dark:border-rp-separator/50"}`}
            >
              <div>
                <span className={`font-medium ${isAlpha ? "text-v7-ink" : "text-gray-900 dark:text-white"}`}>{r.ticker}</span>
                <span className={`ml-2 ${isAlpha ? "text-v7-ink-soft" : "text-gray-500 dark:text-rp-grey"}`}>{r.name}</span>
                {r.primaryExchange && (
                  <span className={`ml-2 text-xs ${isAlpha ? "text-v7-ink/40" : "text-gray-400 dark:text-rp-grey/40"}`}>{r.primaryExchange}</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {status && <StockLiquidityIndicator status={status} appearance={appearance} />}
                {status !== "RED" && (
                  <Plus className={`w-3.5 h-3.5 ${isAlpha ? "text-v7-ink/40" : "text-gray-400 dark:text-rp-grey/60"}`} />
                )}
              </div>
            </button>
            );
          })}
        </div>
      )}

      {gateDialog && (
        <ConfirmationModal
          isOpen
          onClose={() => setGateDialog(null)}
          onConfirm={handleGateConfirm}
          title={STOCK_LIQUIDITY_DIALOG_TITLE[gateDialog.status]}
          message={
            <>
              <p className={`font-medium ${isAlpha ? "text-v7-ink" : "text-gray-900 dark:text-white"}`}>
                {gateDialog.result.ticker}
                {gateDialog.result.name ? ` -- ${gateDialog.result.name}` : ""}
              </p>
              <p className="mt-2">{STOCK_LIQUIDITY_MESSAGE[gateDialog.status]}</p>
            </>
          }
          confirmText="Add anyway"
          dismissOnly={gateDialog.status === "RED"}
          appearance={appearance}
        />
      )}
    </div>
  );
};

export default UnifiedSearchBar;
